import React, { useState } from 'react';
import { Calculator, Scale, DollarSign, Clock, CreditCard, Home } from 'lucide-react';
import VADisabilityCalculator from './VADisabilityCalculator';
import RetirementPayCalculator from './RetirementPayCalculator';
import BackPayCalculator from './BackPayCalculator';
import MilitaryPayCalculator from './MilitaryPayCalculator';
import HomeLoanCalculator from './HomeLoanCalculator';

const CALCULATORS = [
  {
    id: 'disability',
    title: 'VA Disability Rating',
    shortTitle: 'Disability',
    icon: Scale,
    color: 'text-red-600 bg-red-100',
    description: 'Calculate combined VA rating and monthly compensation',
    Component: VADisabilityCalculator,
  },
  {
    id: 'retirement',
    title: 'Military Retirement Pay',
    shortTitle: 'Retirement',
    icon: DollarSign,
    color: 'text-green-600 bg-green-100',
    description: 'Estimate retirement pay across all systems (High-3, BRS, Final)',
    Component: RetirementPayCalculator,
  },
  {
    id: 'backpay',
    title: 'VA Back Pay',
    shortTitle: 'Back Pay',
    icon: Clock,
    color: 'text-amber-600 bg-amber-100',
    description: 'Calculate retroactive compensation from effective to decision date',
    Component: BackPayCalculator,
  },
  {
    id: 'pay',
    title: 'Military Pay',
    shortTitle: 'Pay',
    icon: CreditCard,
    color: 'text-blue-600 bg-blue-100',
    description: 'Total compensation: base pay + BAH + BAS + tax advantage',
    Component: MilitaryPayCalculator,
  },
  {
    id: 'homeloan',
    title: 'VA Home Loan',
    shortTitle: 'Home Loan',
    icon: Home,
    color: 'text-purple-600 bg-purple-100',
    description: 'Monthly payment, funding fee, and VA vs Conventional comparison',
    Component: HomeLoanCalculator,
  },
];

const CalculatorsPage = () => {
  const [activeCalc, setActiveCalc] = useState('disability');
  const active = CALCULATORS.find(c => c.id === activeCalc);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="calculators-page">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Benefits Calculators</h1>
            <p className="text-sm text-slate-500">Interactive tools with real military data and VA formulas</p>
          </div>
        </div>
      </div>

      {/* Calculator Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-5 scrollbar-hide">
        {CALCULATORS.map(calc => {
          const isActive = activeCalc === calc.id;
          return (
            <button
              key={calc.id}
              onClick={() => setActiveCalc(calc.id)}
              data-testid={`calc-tab-${calc.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <calc.icon className={`w-4 h-4 ${isActive ? 'text-white' : ''}`} />
              <span className="hidden sm:inline">{calc.title}</span>
              <span className="sm:hidden">{calc.shortTitle}</span>
            </button>
          );
        })}
      </div>

      {/* Active Calculator */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-5 pb-4 border-b border-slate-100">
          <div className={`w-8 h-8 rounded-lg ${active.color} flex items-center justify-center`}>
            <active.icon className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">{active.title} Calculator</h2>
            <p className="text-xs text-slate-500">{active.description}</p>
          </div>
        </div>
        <active.Component />
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-slate-400 mt-4 text-center">
        These calculators provide estimates based on 2026 rates. Verify with official sources (VA.gov, DFAS) for personalized information. Consult a VSO or financial advisor for major decisions.
      </p>
    </div>
  );
};

export default CalculatorsPage;
